/**
 * Hook para estatísticas do dashboard
 * 
 * Responsabilidade: Calcular estatísticas gerais e motivos de perda
 * Princípios: Single Responsibility, DRY
 */

import { useMemo } from 'react';
import { Lead, Contact } from '../../../types';
import { useLossReasons } from '../../settings/loss-reasons/hooks/useLossReasons';

const formatCurrency = (val: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

export const useDashboardStats = (
    leads: Lead[],
    filteredLeads: Lead[],
    contacts: Contact[]
) => {
    // Estatísticas gerais
    const stats = useMemo(() => {
        const totalLeads = filteredLeads.length;
        const closedDeals = filteredLeads.filter(l => l.status === 'won').length;
        const totalValue = filteredLeads.reduce((acc, curr) => acc + (Number(curr.value) || 0), 0);
        const closedValue = filteredLeads
            .filter(l => l.status === 'won')
            .reduce((acc, curr) => acc + (Number(curr.value) || 0), 0);

        // Período anterior (mock - idealmente seria calculado com query separada)
        const prevLeads = Math.max(0, totalLeads - 5);
        const prevClosed = Math.max(0, closedDeals - 1);
        const prevValue = Math.max(0, totalValue * 0.8);
        const prevClosedValue = Math.max(0, closedValue * 0.7);

        const leadsChange = prevLeads === 0 ? 0 : ((totalLeads - prevLeads) / prevLeads) * 100;
        const closedChange = prevClosed === 0 ? 0 : ((closedDeals - prevClosed) / prevClosed) * 100;
        const valueChange = prevValue === 0 ? 0 : ((totalValue - prevValue) / prevValue) * 100;
        const closedValueChange = prevClosedValue === 0 ? 0 : ((closedValue - prevClosedValue) / prevClosedValue) * 100;

        // Estatísticas de perdas
        const lostDeals = filteredLeads.filter(l => l.status === 'lost').length;
        const lostValue = filteredLeads
            .filter(l => l.status === 'lost')
            .reduce((acc, curr) => acc + (Number(curr.value) || 0), 0);

        const prevLost = Math.max(0, lostDeals - 2);
        const prevLostValue = Math.max(0, lostValue * 0.9);

        const lostChange = prevLost === 0 ? 0 : ((lostDeals - prevLost) / prevLost) * 100;
        const lostValueChange = prevLostValue === 0 ? 0 : ((lostValue - prevLostValue) / prevLostValue) * 100;

        return {
            totalContacts: contacts.length,
            totalNegotiations: leads.length,
            leads: { value: totalLeads.toString(), change: leadsChange, base: prevLeads.toString() },
            closed: { value: closedDeals.toString(), change: closedChange, base: prevClosed.toString() },
            value: {
                value: formatCurrency(totalValue),
                change: valueChange,
                base: formatCurrency(prevValue)
            },
            closedValue: {
                value: formatCurrency(closedValue),
                change: closedValueChange,
                base: formatCurrency(prevClosedValue)
            },
            lost: {
                value: lostDeals.toString(),
                change: lostChange,
                base: prevLost.toString()
            },
            lostValue: {
                value: formatCurrency(lostValue),
                change: lostValueChange,
                base: formatCurrency(prevLostValue)
            }
        };
    }, [filteredLeads, leads, contacts]);

    // Leads por motivo de perda
    const { reasons: lossReasons } = useLossReasons();

    const leadsByLostReason = useMemo(() => {
        const counts: { [key: string]: number } = {};

        filteredLeads.forEach(lead => {
            if (lead.status === 'lost' && lead.lostReasonId) {
                const reason = lossReasons.find(r => r.id === lead.lostReasonId);
                const name = reason ? reason.name : 'Outros';
                counts[name] = (counts[name] || 0) + 1;
            } else if (lead.status === 'lost') {
                counts['Sem motivo'] = (counts['Sem motivo'] || 0) + 1;
            }
        });

        const palette = [
            'var(--color-danger)',
            'var(--color-warning)',
            'var(--color-primary)',
            'var(--color-success)',
        ];

        return Object.entries(counts)
            .map(([name, value], index) => ({
                name,
                value,
                color: palette[index % palette.length]
            }))
            .sort((a, b) => b.value - a.value);
    }, [filteredLeads, lossReasons]);

    return {
        stats,
        leadsByLostReason
    };
};
